'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'

// A modern in-page colour picker that replaces the native
// <input type="color">, whose mobile UX is the clunky OS dialog. A
// compact popover with brand-aware swatches, a hue spectrum and a hex
// field — no OS modal, fully keyboard- and screen-reader-operable.

const DEFAULT_SWATCHES = [
  '#CDEB45', '#0D1424', '#2F5BFF', '#16A34A', '#F59E0B', '#EF4444',
  '#EC4899', '#8B5CF6', '#0EA5E9', '#14B8A6', '#FFFFFF', '#64748B',
]

const isHex = (v: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)

function hslToHex(h: number, s: number, l: number) {
  s /= 100; l /= 100
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  const to = (x: number) => Math.round(255 * x).toString(16).padStart(2, '0')
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`.toUpperCase()
}

export function ColorPicker({
  value,
  onChange,
  swatches = DEFAULT_SWATCHES,
  id,
  ariaLabel = 'Choose colour',
}: {
  value: string
  onChange: (hex: string) => void
  swatches?: string[]
  id?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = React.useState(false)
  const [hex, setHex] = React.useState(value)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => setHex(value), [value])

  React.useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const commit = (v: string) => {
    const u = v.toUpperCase()
    setHex(u)
    if (isHex(u)) onChange(u)
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        id={id}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${ariaLabel}, current ${hex}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="h-5 w-5 rounded-md border" style={{ backgroundColor: isHex(hex) ? hex : '#ffffff' }} aria-hidden />
        <span className="tabular-nums">{hex}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={ariaLabel}
          className="absolute left-0 z-50 mt-2 w-60 rounded-xl border bg-popover p-3 shadow-xl"
        >
          <div className="grid grid-cols-6 gap-2">
            {swatches.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => commit(s)}
                aria-label={s}
                aria-pressed={value.toUpperCase() === s.toUpperCase()}
                className={cn(
                  'h-7 w-7 rounded-md border transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  value.toUpperCase() === s.toUpperCase() && 'ring-2 ring-primary ring-offset-1',
                )}
                style={{ backgroundColor: s }}
              />
            ))}
          </div>

          <label className="mt-3 block">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Spectrum</span>
            <input
              type="range"
              min={0}
              max={360}
              aria-label="Hue"
              onChange={(e) => commit(hslToHex(Number(e.target.value), 80, 55))}
              className="mt-1 h-3 w-full cursor-pointer appearance-none rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ background: 'linear-gradient(to right,#ff0000,#ffff00,#00ff00,#00ffff,#0000ff,#ff00ff,#ff0000)' }}
            />
          </label>

          <div className="mt-3 flex items-center gap-2">
            <label htmlFor={`${id ?? 'cp'}-hex`} className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Hex
            </label>
            <input
              id={`${id ?? 'cp'}-hex`}
              value={hex}
              onChange={(e) => commit(e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`)}
              maxLength={7}
              spellCheck={false}
              autoComplete="off"
              className="w-24 rounded-md border bg-background px-2 py-1 text-xs tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="ml-auto h-6 w-6 rounded-md border" style={{ backgroundColor: isHex(hex) ? hex : '#ffffff' }} aria-hidden />
          </div>
        </div>
      )}
    </div>
  )
}
